import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowLeft, LogOut, ExternalLink } from "lucide-react";
import { Logo } from "@/components/vaulted";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/layout/PublicLayout";
import { hasRole, can } from "@/lib/api";
import { cn } from "@/lib/utils";

export function RequireAuth({ roles, staff, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" /></div>;
  const isStaff = user && user.account_type === "staff";
  if (staff) return isStaff ? children : <Navigate to="/staff/login" replace />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (isStaff) return <Navigate to="/admin" replace />;
  if (roles && !hasRole(user, ...roles)) return <Navigate to="/account" replace />;
  return children;
}

const topRole = (u) => ["OWNER", "ADMIN", "STAFF", "SELLER", "CUSTOMER"].find((r) => u?.roles?.includes(r));

function SideNav({ nav, onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {nav.map((n) => n.section ? (
        <p key={n.section} className="mt-5 mb-1.5 px-3 text-[10px] font-medium uppercase tracking-[0.16em] text-zinc-600">{n.section}</p>
      ) : (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`dash-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
          className={({ isActive }) => cn("group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors",
            isActive ? "bg-violet-500/[0.12] text-white ring-1 ring-inset ring-violet-500/25" : "text-zinc-400 hover:bg-white/[0.03] hover:text-white")}>
          {({ isActive }) => (<><n.icon className={cn("h-4 w-4", isActive ? "text-violet-400" : "text-zinc-500 group-hover:text-zinc-300")} />{n.label}</>)}
        </NavLink>
      ))}
    </nav>
  );
}

const allowedNav = (nav, user) => {
  const items = nav.filter((n) => n.section || (n.adminOnly ? hasRole(user, "ADMIN", "OWNER") : !n.perm || can(user, n.perm)));
  return items.filter((n, i) => !n.section || (items[i + 1] && !items[i + 1].section));
};

function StaffLayout({ title, nav: fullNav, testId }) {
  const { user, logout } = useAuth();
  const nav = allowedNav(fullNav, user);
  const navTo = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const match = (list) => list.filter((n) => n.to).sort((a, b) => b.to.length - a.to.length).find((n) => loc.pathname.startsWith(n.to));
  const current = match(nav);
  const blocked = match(fullNav) !== current;
  const firstAllowed = nav.find((n) => n.to);
  return (
    <div className="min-h-screen bg-[#09090B] lg:flex" data-testid={testId}>
      <aside className={cn("fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-[#1f1f23] bg-[#0b0b0e] transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0", open ? "translate-x-0" : "-translate-x-full")}>
        <div className="flex h-16 items-center justify-between px-5 border-b border-[#1f1f23]">
          <Logo />
          <button className="lg:hidden text-zinc-400" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 pt-4"><span data-testid="panel-title" className="inline-flex rounded-md border border-violet-500/25 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-300">{title}</span></div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-4"><SideNav nav={nav} onNavigate={() => setOpen(false)} /></div>
        <div className="border-t border-[#1f1f23] p-3">
          <div className="flex items-center gap-3 rounded-lg bg-[#111116] p-2.5">
            <Avatar user={user} size={8} />
            <div className="min-w-0 flex-1"><p className="truncate text-sm text-white">{user?.username}</p><p className="text-[10px] uppercase tracking-wider text-violet-300">{topRole(user)}</p></div>
            <button data-testid="panel-logout" onClick={async () => { const s = user?.account_type === "staff"; await logout(); navTo(s ? "/staff/login" : "/"); }} className="text-zinc-500 hover:text-red-400" title="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-[#1f1f23] bg-[#09090B]/85 px-4 backdrop-blur-xl sm:px-6">
          <button className="lg:hidden text-zinc-300" onClick={() => setOpen(true)} data-testid="dash-mobile-toggle"><Menu className="h-5 w-5" /></button>
          <div className="text-sm"><span className="text-zinc-500">{title}</span><span className="mx-2 text-zinc-700">/</span><span className="text-white" data-testid="panel-breadcrumb">{current?.label}</span></div>
          <div className="ml-auto flex items-center gap-2">
            {hasRole(user, "SELLER") && title !== "Seller Panel" && <Link to="/seller" className="v-btn-ghost v-btn-sm hidden sm:inline-flex" data-testid="switch-seller-panel">Seller Panel</Link>}

            <Link to="/" data-testid="dash-back-to-store" className="v-btn-ghost v-btn-sm"><ExternalLink className="h-3.5 w-3.5" />View store</Link>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><div className="mx-auto max-w-7xl rise" key={loc.pathname}>
          {!blocked ? <Outlet /> : firstAllowed ? <Navigate to={firstAllowed.to} replace /> :
            <div className="v-panel p-10 text-center" data-testid="no-staff-access"><p className="text-white">No staff areas assigned</p><p className="mt-1 text-sm text-zinc-500">Ask an admin to give you a staff role.</p></div>}
        </div></main>
      </div>
    </div>
  );
}

export function DashboardLayout({ title, nav, testId, variant }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (variant === "staff") return <StaffLayout title={title} nav={nav} testId={testId} />;
  return (
    <div className="min-h-screen bg-[#09090B]" data-testid={testId}>
      <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-[#27272A] bg-[#09090B]/90 px-4 backdrop-blur-xl lg:px-6">
        <button className="lg:hidden text-zinc-300" onClick={() => setOpen(!open)} data-testid="dash-mobile-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        <Logo />
        <span className="hidden sm:inline rounded border border-[#27272A] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-zinc-400">{title}</span>
        <div className="ml-auto flex items-center gap-3">
          <Link to="/marketplace" data-testid="dash-back-to-store" className="hidden sm:flex items-center gap-1.5 text-xs v-link"><ArrowLeft className="h-3.5 w-3.5" />Back to store</Link>
          <Avatar user={user} size={7} />
        </div>
      </header>
      <div className="flex">
        <aside className={cn("fixed inset-y-14 left-0 z-30 w-60 shrink-0 overflow-y-auto border-r border-[#27272A] bg-[#0b0b0e] p-3 transition-transform lg:sticky lg:top-14 lg:h-[calc(100vh-3.5rem)] lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full")}><SideNav nav={nav} onNavigate={() => setOpen(false)} /></aside>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8"><div className="mx-auto max-w-6xl rise"><Outlet /></div></main>
      </div>
    </div>
  );
}
